import { useState, useCallback } from "react";
import { AsyncODataDropdownFieldValue } from "../../Fields/types/fieldTypes";
import { useOData } from "../../../stores/OData/useOData";

export interface PdfTemplatePreviewModule {
  // State
  formId: string | undefined;
  formData: Record<string, string> | null;

  // Methods
  handleChange: (value: AsyncODataDropdownFieldValue) => Promise<void>;
  setFormId: (formId?: string) => Promise<void>;
  setFormData: (data: Record<string, string>) => void;
  setup: () => Promise<void>;
}

export const usePdfTemplatePreviewToolbar = (
  previewAnnotations: (data?: any) => Promise<void>,
  getTemplateFieldsData?: (
    formId?: string
  ) => Promise<Record<string, string> | {}>
): PdfTemplatePreviewModule => {
  // State
  const [formId, setFormIdState] = useState<string | undefined>();
  const [formData, setFormDataState] = useState<Record<string, string> | {}>(
    {}
  );

  // OData hook
  const {
    init,
    setupAsyncODataDropdownDataManager,
    baseActionFailureEvent,
    baseActionCompleteEvent,
  } = useOData(
    { getAuthRequestHeader: () => Promise.resolve(undefined) },
    () => {
      // Initialize callback - no parameters needed
    }
  );

  const handleChange = async (value: AsyncODataDropdownFieldValue) => {
    if (!value) {
      await setFormId(undefined);
    } else {
      await setFormId(value as string);
    }
  };

  const setFormData = useCallback((data: Record<string, string> | {}) => {
    setFormDataState(data);
  }, []);

  const setFormId = useCallback(
    async (formIdParam?: string) => {
      setFormIdState(formIdParam);
      let templateFieldsData: Record<string, string> | {} = {};
      if (formIdParam) {
        templateFieldsData = (await getTemplateFieldsData?.(formIdParam)) || {};
        setFormData(templateFieldsData);
      }
      await previewAnnotations(templateFieldsData);
    },
    [previewAnnotations, setFormData]
  );

  const setup = useCallback(async () => {
    await init?.();
  }, [
    init,
    setupAsyncODataDropdownDataManager,
    formId,
    handleChange,
    baseActionFailureEvent,
    baseActionCompleteEvent,
  ]);

  return {
    formId,
    formData,
    handleChange,
    setFormId,
    setFormData,
    setup,
  };
};
